import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Phone, ClipboardList, ScanLine } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Seo, physicianSchema } from "@/components/Seo";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { SITE } from "@/site";

const GROUPS = [
  {
    id: "digestive-disorders",
    name: "Digestive Disorders",
    blurb:
      "Stomach, intestine and bowel problems — the most common reasons patients visit the clinic.",
    hindi: "पेट और आंतों की समस्याएँ — एसिडिटी, अल्सर, IBS, कब्ज़ और दस्त।",
    items: [
      {
        name: "Acidity & GERD (Reflux)",
        text: "Burning in the chest, sour belching or a sour taste in the mouth is usually gastro-oesophageal reflux (GERD). Treatment combines medicines with practical changes in food timing and habits. Most patients improve within a few weeks. If symptoms continue or warning signs appear, an endoscopy is advised.",
        hindi: "खट्टी डकारें और सीने में जलन का कारण जानकर सही इलाज।",
      },
      {
        name: "Peptic Ulcer Disease",
        text: "Ulcers in the stomach or duodenum usually cause upper abdominal pain related to meals. We test for H. pylori infection, which is a common and fully treatable cause. A proper course of medicines heals most ulcers completely, and advice on diet and painkillers prevents them from coming back.",
        hindi: "पेट के अल्सर की जाँच (H. pylori सहित) और पूरा इलाज।",
      },
      {
        name: "Irritable Bowel Syndrome (IBS)",
        text: "IBS causes repeated abdominal discomfort with constipation, loose motions or both, without any damage to the intestine. Diagnosis is made clinically after ruling out other causes where needed. Treatment focuses on diet, stress management and targeted medicines. It is a long-term condition, but it can be kept well under control.",
        hindi: "पेट में गैस, दर्द और दस्त-कब्ज़ की बार-बार की समस्या का प्रबंधन।",
      },
      {
        name: "Inflammatory Bowel Disease (IBD)",
        text: "Ulcerative colitis and Crohn's disease cause ongoing inflammation of the intestine. Symptoms include blood in stool, long-term diarrhoea and weight loss. These conditions need proper diagnosis with colonoscopy and biopsy. Modern medicines control the disease well in most patients, and regular follow-up prevents flare-ups.",
        hindi: "आंतों की सूजन (अल्सरेटिव कोलाइटिस, क्रोन्स रोग) का विशेषज्ञ इलाज।",
      },
      {
        name: "Chronic Constipation",
        text: "Constipation lasting for months deserves a proper evaluation instead of endless home remedies. We review diet, water intake, medicines and, when needed, check for thyroid or gut motility problems. Treatment is stepwise, starting with the simplest effective measures.",
        hindi: "लंबे समय की कब्ज़ का कारण खोजकर इलाज।",
      },
      {
        name: "Chronic Diarrhoea",
        text: "Loose motions lasting more than a few weeks can be due to infection, IBS, IBD or poor absorption of food. Stool tests, blood tests and, when needed, endoscopy help find the exact cause. Treatment is then directed at the cause, not just the symptom.",
        hindi: "लगातार दस्तों की सही जाँच और कारण अनुसार इलाज।",
      },
      {
        name: "Celiac Disease & Malabsorption",
        text: "Unexplained anaemia, weight loss or chronic diarrhoea can mean the intestine is not absorbing food properly. Blood tests and an endoscopic biopsy confirm conditions like celiac disease. A structured, gluten-free diet plan then restores health in most patients.",
        hindi: "खाना सही से पच न पाने (अवशोषण की कमी) की जाँच और इलाज।",
      },
      {
        name: "Difficulty Swallowing (Dysphagia)",
        text: "Trouble swallowing food or water should never be ignored. An endoscopy shows whether there is a narrowing, inflammation or a growth in the food pipe. Finding the cause early makes treatment much simpler and more effective.",
        hindi: "निगलने में तकलीफ़ हो तो तुरंत जाँच कराएँ।",
      },
    ],
  },
  {
    id: "liver-diseases",
    name: "Liver Diseases",
    blurb:
      "From fatty liver to cirrhosis — early evaluation makes the biggest difference to outcomes.",
    hindi: "फैटी लीवर से लेकर सिरोसिस तक — समय पर जाँच सबसे ज़रूरी है।",
    items: [
      {
        name: "Fatty Liver (NAFLD / MASLD)",
        text: "Fatty liver is now extremely common and is usually found on an ultrasound. We grade its severity and check for inflammation or early scarring. Weight management, diet, and control of sugar and cholesterol form the base of treatment, with medicines where needed.",
        hindi: "लीवर में चर्बी जमने की सही जाँच, ग्रेडिंग और इलाज।",
      },
      {
        name: "Hepatitis B & C",
        text: "These viral infections can damage the liver silently for years without symptoms. Simple blood tests detect them, and effective antiviral treatment is available — hepatitis C is now curable in almost all cases. Screening of family members is advised where relevant.",
        hindi: "हेपेटाइटिस बी और सी की जाँच और प्रभावी दवाओं से इलाज।",
      },
      {
        name: "Jaundice Evaluation",
        text: "Yellowing of the eyes or urine always needs prompt evaluation to find the cause — viral hepatitis, gallstones or a blockage in the bile duct. Blood tests and ultrasound usually give the answer quickly. Treatment then targets the specific cause.",
        hindi: "पीलिया के कारण की त्वरित जाँच और उसी अनुसार इलाज।",
      },
      {
        name: "Cirrhosis & Its Complications",
        text: "Long-standing liver damage can lead to cirrhosis, which carries risks like fluid in the abdomen, vomiting of blood and confusion. Regular follow-up, endoscopic screening, vaccines and medicines help prevent these complications. Patients are guided clearly on diet, salt and warning signs.",
        hindi: "सिरोसिस और उसकी जटिलताओं (पेट में पानी, खून की उल्टी) का नियमित इलाज।",
      },
      {
        name: "Alcohol-related Liver Disease",
        text: "Liver damage from alcohol is treatable, especially when caught early. We give honest, non-judgemental guidance on stopping safely, along with nutritional support and regular monitoring. With complete abstinence, the liver often recovers remarkably well.",
        hindi: "शराब से लीवर को हुए नुकसान का इलाज और सही मार्गदर्शन।",
      },
    ],
  },
  {
    id: "pancreatic-gallbladder",
    name: "Pancreatic & Gallbladder",
    blurb:
      "Careful diagnosis first; surgery is advised only when it is genuinely needed.",
    hindi: "पहले सही जाँच — ऑपरेशन की सलाह तभी, जब सच में ज़रूरी हो।",
    items: [
      {
        name: "Gallstones",
        text: "Gallstones are common, and not all of them need surgery. We evaluate your symptoms, the size of the stones and any complications before advising. If surgery is needed, we coordinate with experienced laparoscopic surgeons and manage your care before and after the operation.",
        hindi: "पित्त की पथरी — ज़रूरत हो तभी ऑपरेशन की सलाह।",
      },
      {
        name: "Acute & Chronic Pancreatitis",
        text: "Pancreatitis causes severe pain in the upper abdomen and needs timely treatment. We identify the cause — gallstones, alcohol or others — so that repeated attacks can be prevented. Chronic pancreatitis is managed with enzyme supplements, diet and pain control.",
        hindi: "अग्न्याशय की सूजन का कारण पता करके इलाज।",
      },
      {
        name: "Bile Duct Obstruction",
        text: "A blocked bile duct causes jaundice, itching and sometimes fever with chills. Imaging tests pinpoint the blockage, and the further plan — an endoscopic procedure or surgery — is decided accordingly. Prompt treatment prevents serious infection and liver damage.",
        hindi: "पित्त नली में रुकावट की जाँच और समय पर उपचार।",
      },
    ],
  },
  {
    id: "diagnostic-procedures",
    name: "Diagnostic & Therapeutic Procedures",
    blurb:
      "Accurate diagnosis with a gentle, safety-first approach to every procedure.",
    hindi: "एंडोस्कोपी और कोलोनोस्कोपी — सुरक्षित और आरामदायक तरीके से।",
    items: [
      {
        name: "Upper GI Endoscopy",
        text: "A thin, flexible camera is used to examine the food pipe, stomach and duodenum. The throat is numbed with a spray first, and the procedure takes only a few minutes. It gives a direct and reliable answer for acidity, pain, bleeding or difficulty in swallowing.",
        hindi: "कुछ ही मिनट में खाने की नली और पेट की सीधी जाँच।",
      },
      {
        name: "Colonoscopy",
        text: "Colonoscopy examines the entire large intestine. It is the most reliable test for bleeding from the rectum, chronic diarrhoea and cancer screening. The bowel preparation is explained clearly in advance, and sedation options keep the procedure comfortable.",
        hindi: "बड़ी आंत की पूरी और सबसे भरोसेमंद जाँच।",
      },
      {
        name: "Endoscopic Biopsy",
        text: "During endoscopy or colonoscopy, tiny tissue samples can be taken without any pain. These samples confirm conditions like celiac disease, H. pylori infection or early cancers. Every report is explained in person, in plain language.",
        hindi: "जाँच के दौरान बिना दर्द के छोटा टिश्यू सैंपल लेना।",
      },
      {
        name: "Variceal Banding",
        text: "In patients with cirrhosis, enlarged veins in the food pipe can bleed dangerously. Banding done during endoscopy seals these veins and prevents bleeding. It is a day-care procedure, usually done in a few short sessions.",
        hindi: "सिरोसिस में नसों से खून बहने रोकने की प्रक्रिया।",
      },
      {
        name: "Polypectomy (Polyp Removal)",
        text: "Polyps found during a colonoscopy are usually removed in the same sitting. Removing them prevents the small risk of these polyps turning into cancer later. The removed tissue is always sent for laboratory examination.",
        hindi: "आंत में पॉलिप मिलने पर उसी बैठक में सुरक्षित निष्कासन।",
      },
      {
        name: "Upper GI Bleeding Control",
        text: "Vomiting blood or passing black, tarry stools is an emergency. Endoscopy finds the exact source of bleeding, and in most cases the bleeding can be stopped during the same procedure using clips, bands or injections.",
        hindi: "खून की उल्टी या काला मल — एंडोस्कोपी से तुरंत रोकथाम।",
      },
      {
        name: "Liver Fibrosis Assessment",
        text: "Non-invasive tests, including elastography (such as FibroScan) and blood-based scores, measure liver stiffness and scarring. In most cases this removes the need for a liver biopsy. Repeat measurements over time show whether treatment is working.",
        hindi: "बिना बायोप्सी के लीवर की स्कैरिंग (फाइब्रोसिस) की माप।",
      },
    ],
  },
];

const PREP = [
  {
    icon: ScanLine,
    title: "Before an Endoscopy",
    points: [
      "Do not eat or drink anything for 6–8 hours before the procedure.",
      "Tell us about your regular medicines (especially blood thinners) while booking.",
      "Bring one attendant with you if sedation is planned.",
      "You can usually go home within 30–60 minutes after the test.",
    ],
    hindi: "एंडोस्कोपी से 6–8 घंटे पहले कुछ न खाएँ-पिएँ।",
  },
  {
    icon: ClipboardList,
    title: "Before a Colonoscopy",
    points: [
      "A bowel-cleansing solution is prescribed for the day before — follow the instructions given to you.",
      "Eat light, low-fibre food for 1–2 days before the test.",
      "Arrange one attendant to accompany you home.",
      "Bring all previous reports and prescriptions.",
    ],
    hindi: "कोलोनोस्कोपी से एक दिन पहले पेट साफ़ करने की दवा दी जाती है।",
  },
];

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.domain}/` },
    { "@type": "ListItem", position: 2, name: "Treatments", item: `${SITE.domain}/treatments` },
  ],
};

export default function Treatments() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        const t = setTimeout(
          () => el.scrollIntoView({ behavior: "instant", block: "start" }),
          120
        );
        return () => clearTimeout(t);
      }
    }
  }, [hash]);

  return (
    <>
      <Seo
        title="Treatments — Gastro, Liver, Endoscopy & Colonoscopy | Dr. Dixit, Varanasi"
        description="All treatments by Dr. Dixit, DM (Gastro) IMS BHU, Varanasi: acidity & GERD, ulcers, IBS, IBD, constipation, fatty liver, hepatitis B & C, jaundice, cirrhosis, gallstones, pancreatitis, endoscopy, colonoscopy and polyp removal."
        path="/treatments"
        jsonLd={[physicianSchema, breadcrumbSchema]}
      />

      <section className="hero-grid-lines bg-gradient-to-b from-white to-sky-50/60 border-b border-slate-100">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 pt-14 pb-14 sm:pt-20 sm:pb-16">
          <p
            data-testid="treatments-eyebrow"
            className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-600"
          >
            Treatments &amp; Procedures
          </p>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-navy">
            <MaskedLine delay={0.1}>Every treatment,</MaskedLine>
            <MaskedLine delay={0.25}>
              <span className="text-sky-600">explained in simple words.</span>
            </MaskedLine>
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Each condition below is described in plain language — what it is,
            how it is diagnosed, and how it is treated. No medical jargon.
          </p>
          <p className="mt-3 max-w-2xl font-hindi text-base text-slate-500 leading-relaxed">
            हर बीमारी की जानकारी सरल भाषा में — क्या है, कैसे जाँच होती है और
            इलाज कैसे होता है।
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-16 sm:py-20" data-testid="treatments-list">
        <div className="space-y-14">
          {GROUPS.map((group, gi) => (
            <Reveal key={group.name} delay={0.05}>
              <div
                id={group.id}
                data-testid={`treatments-accordion-group-${gi}`}
                className="scroll-mt-28"
              >
                <div className="flex items-baseline gap-4">
                  <span className="font-heading text-sm font-bold text-teal-600">
                    {String(gi + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-navy">
                      {group.name}
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">{group.blurb}</p>
                    <p className="mt-1 font-hindi text-sm text-slate-500">
                      {group.hindi}
                    </p>
                  </div>
                </div>
                <Accordion type="single" collapsible className="mt-5 space-y-3">
                  {group.items.map((item, ii) => (
                    <AccordionItem
                      key={item.name}
                      value={`g${gi}-i${ii}`}
                      data-testid={`treatment-item-${gi}-${ii}`}
                      className="rounded-xl border border-slate-200 bg-white px-5 transition-colors data-[state=open]:border-sky-300"
                    >
                      <AccordionTrigger className="py-4 text-left font-heading text-base font-semibold text-navy hover:no-underline [&[data-state=open]>svg]:rotate-180">
                        {item.name}
                      </AccordionTrigger>
                      <AccordionContent className="pb-5">
                        <p className="text-base leading-relaxed text-slate-600">
                          {item.text}
                        </p>
                        <p className="mt-3 font-hindi text-sm leading-relaxed text-slate-500 border-l-2 border-sky-200 pl-3">
                          {item.hindi}
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-slate-50/70 border-y border-slate-100" data-testid="prep-guide-section">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-16 sm:py-20">
          <Reveal>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-navy">
              Preparing for your procedure
            </h2>
            <p className="mt-3 max-w-2xl text-base text-slate-600 leading-relaxed">
              Good preparation makes endoscopy and colonoscopy quick and
              comfortable. The key points are below; full written instructions
              are given when you book.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {PREP.map((p, i) => (
              <Reveal key={p.title} delay={0.08 * i}>
                <div
                  data-testid={`prep-card-${i}`}
                  className="h-full rounded-2xl border border-slate-200 bg-white p-7"
                >
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                    <p.icon size={22} />
                  </div>
                  <h3 className="mt-4 font-heading text-xl font-semibold text-navy">
                    {p.title}
                  </h3>
                  <ul className="mt-4 space-y-2.5">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex gap-3 text-sm leading-relaxed text-slate-600">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-500" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 font-hindi text-sm text-slate-500 border-l-2 border-sky-200 pl-3">
                    {p.hindi}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy" data-testid="treatments-cta">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-16 text-center">
          <Reveal>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white">
              Not sure which specialist your problem needs?
            </h2>
            <p className="mt-3 text-base text-slate-400">
              Call and describe your symptoms — you will be guided honestly.
            </p>
            <p className="mt-2 font-hindi text-sm text-slate-400">
              कॉल करें और अपनी समस्या बताएँ — सही सलाह दी जाएगी।
            </p>
            <a
              href={SITE.phoneHref}
              data-testid="treatments-call-button"
              className="mt-7 inline-flex items-center gap-3 rounded-full bg-sky-500 px-8 py-4 font-heading text-lg font-bold text-white transition-all duration-200 hover:bg-sky-400 hover:-translate-y-0.5"
            >
              <Phone size={19} strokeWidth={2.5} />
              Call {SITE.phoneDisplay}
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
}
